import { useState, useCallback } from 'react';

/**
 * Undo/redo history manager for the report builder.
 *
 * Each history entry is `{ label, ts, snap }` where `snap` is a serialized
 * snapshot of whatever `snapshot()` returns. The optional `label` powers the
 * "Recent Edits" peek (a human description of the change). On undo/redo/revert
 * we call `restore(state)` with the deserialized snapshot so the caller can
 * wire state back into React.
 */
const makeEntry = (snap, label) => ({ label: label || 'Edit', ts: Date.now(), snap });
const snapOf = (entry) => (typeof entry === 'string' ? entry : entry.snap);

export function useReportUndoRedo({ snapshot, restore, onChange, maxDepth = 30 }) {
  const [undoStack, setUndoStack] = useState([]);
  const [redoStack, setRedoStack] = useState([]);

  const pushUndo = useCallback((label) => {
    setUndoStack(prev => [...prev.slice(-(maxDepth - 1)), makeEntry(JSON.stringify(snapshot()), label)]);
    setRedoStack([]);
  }, [snapshot, maxDepth]);

  const undo = useCallback(() => {
    setUndoStack(prev => {
      if (prev.length === 0) return prev;
      const entry = prev[prev.length - 1];
      const state = JSON.parse(snapOf(entry));
      setRedoStack(r => [...r, makeEntry(JSON.stringify(snapshot()), entry.label)]);
      restore(state);
      if (onChange) onChange();
      return prev.slice(0, -1);
    });
  }, [snapshot, restore, onChange]);

  const redo = useCallback(() => {
    setRedoStack(prev => {
      if (prev.length === 0) return prev;
      const entry = prev[prev.length - 1];
      const state = JSON.parse(snapOf(entry));
      setUndoStack(u => [...u, makeEntry(JSON.stringify(snapshot()), entry.label)]);
      restore(state);
      if (onChange) onChange();
      return prev.slice(0, -1);
    });
  }, [snapshot, restore, onChange]);

  // Revert directly to the state captured *before* the edit at `targetIndex`
  // in the undo stack (used by the Recent Edits peek). All edits from that
  // point forward are rolled back in one click; a single redo restores them.
  const revertTo = useCallback((targetIndex) => {
    setUndoStack(prev => {
      if (targetIndex < 0 || targetIndex >= prev.length) return prev;
      const state = JSON.parse(snapOf(prev[targetIndex]));
      setRedoStack(r => [...r, makeEntry(JSON.stringify(snapshot()), 'Before revert')]);
      restore(state);
      if (onChange) onChange();
      return prev.slice(0, targetIndex);
    });
  }, [snapshot, restore, onChange]);

  return { undoStack, redoStack, pushUndo, saveUndoState: pushUndo, undo, redo, revertTo };
}
